import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { askConfirm } from '../../components/dialogs';
import { toast } from '../../lib/toast';

export interface RunningTimer {
  userId: string;
  phaseId: string;
  startedAt: string;
  phase: { id: string; type: string; propItem: { id: string; name: string; project: { code: string } } };
}

export function useTimer() {
  return useQuery({
    queryKey: ['timer'],
    queryFn: () => api.get<{ timer: RunningTimer | null }>('/api/timer').then((r) => r.timer),
    refetchInterval: 60_000,
  });
}

function useTimerMutation<T, R>(fn: (v: T) => Promise<R>, onDone?: (r: R) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      onDone?.(r);
      qc.invalidateQueries({ queryKey: ['timer'] });
      qc.invalidateQueries({ queryKey: ['board'] });   // aktualizuj odpracované minuty
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

interface TimerStart {
  timer: RunningTimer;
  previous: { minutes: number; propItemName: string } | null;
}

const writtenLabel = (minutes: number) => (minutes > 0 ? `zapsáno ${minutes} min` : 'nic se nezapsalo');

const useStartTimer = () =>
  useTimerMutation(
    (phaseId: string) => api.post<TimerStart>('/api/timer/start', { phaseId }),
    (r) => {
      if (r.previous) toast.info(`Stopky přepnuty, ${r.previous.propItemName}: ${writtenLabel(r.previous.minutes)}`);
      else toast.success('Stopky běží');
    },
  );

// Stopky běží na jiné fázi: přepnutí se potvrzuje, omyl by jinak tiše ukončil rozdělanou práci.
export function useStartTimerSafe() {
  const { data: running } = useTimer();
  const start = useStartTimer();
  const run = async (phaseId: string, name: string) => {
    if (running && running.phaseId !== phaseId) {
      const ok = await askConfirm({
        title: 'Přepnout stopky?',
        body: `Běží ${running.phase.propItem.name}, ${elapsedLabel(running.startedAt, Date.now())}. Čas se zapíše a stopky se přepnou na výrobu ${name}.`,
        confirmLabel: 'Přepnout',
        cancelLabel: 'Nechat běžet',
      });
      if (!ok) return;
    }
    start.mutate(phaseId);
  };
  return { run, isPending: start.isPending };
}
export const useStopTimer = () =>
  useTimerMutation(
    (note?: string) => api.post<{ minutes: number }>('/api/timer/stop', note ? { note } : {}),
    (r) => toast.success(r.minutes > 0 ? `Čas zapsán, ${r.minutes} min` : 'Běželo to méně než minutu, nic se nezapsalo'),
  );

// Blokace nebo konec fáze zastaví stopky, které na ní běží. Řekneme to, ať je to vidět.
export interface StoppedTimers {
  stoppedTimers?: { fullName: string; minutes: number }[];
}

export function announceStopped(r: StoppedTimers | undefined) {
  const stopped = r?.stoppedTimers ?? [];
  if (stopped.length === 1) toast.info(`Stopky zastaveny, ${writtenLabel(stopped[0].minutes)}`);
  else if (stopped.length > 1) toast.info(`Stopky zastaveny: ${stopped.map((t) => `${t.fullName}, ${writtenLabel(t.minutes)}`).join('; ')}`);
}

// „1 h 23 min" z ISO začátku.
export function elapsedLabel(startedAt: string, now: number): string {
  const min = Math.max(0, Math.floor((now - +new Date(startedAt)) / 60_000));
  const h = Math.floor(min / 60);
  return h > 0 ? `${h} h ${min % 60} min` : `${min} min`;
}
